import { textures } from './textures';
import { STROKE_WIDTH } from './enum';
import { EDGE_STRIP_SEGMENTS } from '../../components/scene/edge-instances.ts';

//triangle-strip vertices per edge stroke instance; derived from the segment
//count so this stays in sync with EDGE_STRIP_CORNERS + the shaders' STRIP_SEGMENTS
const EDGE_STRIP_VERTS = 2 * (EDGE_STRIP_SEGMENTS + 1);

const hitmapScale = textures.hitmap.uniforms.textureScalingFactor;
const hitmapDownScale = textures.pointHitmapDownsampled.uniforms.textureScalingFactor;

const maxPointSize = { uniformType: '1f', defaultValues: [80.0] };

const pointCulledUniforms = {
    pointOpacity: { uniformType: '1f', defaultValues: [0.8] },
    stroke: { uniformType: '1f', defaultValues: [-STROKE_WIDTH] },
    zoomScalingFactor: { uniformType: '1f', defaultValues: [1.0] },
    maxPointSize,
    minPointSize: { uniformType: '1f', defaultValues: [8.0] }
};

//overlay/composite items opt out of the depth planes (pointdepth prepass /
//arrow / stroke z, see the vertex shaders) and blend in render-list order
const drawInListOrder = { disable: [['DEPTH_TEST'], ['CULL_FACE']] };

const pickingGlOpts = {
    clearColor: [[1, 1, 1, 0.0]],
    enable: [['DEPTH_TEST']],
    disable: [['BLEND']]
};

export const items = {
    radialaxes: {
        program: 'radialaxes',
        triggers: ['panZoom', 'renderSceneFast', 'renderSceneFull'],
        bindings: {
            aPos: ['radialAxes', 'pos'],
            aCenter: ['radialAxes', 'center'],
            aRadius: ['radialAxes', 'radius'],
            aFlags: ['radialAxes', 'flags']
        },
        uniforms: {
            stroke: { uniformType: '1f', defaultValues: [STROKE_WIDTH * 0.5] },
            maxCanvasSize: { uniformType: '1f', defaultValues: [1.0] },
            maxScreenSize: { uniformType: '1f', defaultValues: [1.0] },
            edgeZoomScalingFactor: { uniformType: '1f', defaultValues: [1.0] }
        },
        drawType: 'TRIANGLES'
    },
    midedgeculled: {
        program: 'midedgeculled',
        triggers: ['panZoom', 'renderSceneFull'],
        bindings: {
            curPos: ['midSpringsPos', 'curPos'],
            edgeColor: ['midEdgesColors', 'midEdgeColor'],
            startPos: ['midSpringsStarts', 'startPos'],
            endPos: ['midSpringsEnds', 'endPos']
        },
        otherBuffers: {
            logicalEdges: ['logicalEdges', 'curIdx'],
            forwardsEdgeToUnsortedEdge: [
                'forwardsEdgeToUnsortedEdge',
                'forwardsEdgeToUnsortedEdge'
            ],
            edgeColor: ['edgeColors', 'edgeColor'],
            startPos: ['midSpringsStarts', 'startPos'],
            endPos: ['midSpringsEnds', 'endPos']
        },
        uniforms: {
            edgeOpacity: { uniformType: '1f', defaultValues: [0.3] },
            isOpaque: { uniformType: '1f', defaultValues: [0.0] }
        },
        drawType: 'LINES',
        glOptions: {}
    },
    //Instanced analytic-SDF edges: the default edge renderer, replacing
    //midedgeculled (?legacyRenderer falls back; see components/scene/edge-instances.ts)
    edgesdf: {
        program: 'edgesdf',
        triggers: ['panZoom', 'renderSceneFast', 'renderSceneFull'],
        bindings: {
            stripCoord: ['edgeStripCorners', 'stripCoord'],
            srcNodeId: ['edgeEndpointIds', 'srcNodeId'],
            dstNodeId: ['edgeEndpointIds', 'dstNodeId'],
            srcColor: ['edgeStyleAttrs', 'srcColor'],
            dstColor: ['edgeStyleAttrs', 'dstColor'],
            shapeFactor: ['edgeStyleAttrs', 'shapeFactor'],
            dstPointSize: ['edgeStyleAttrs', 'dstPointSize']
        },
        //keep streaming the server buffers the scheduler builds instances from
        //(midedgeculled declared these; picking + arrows also depend on them)
        otherBuffers: {
            logicalEdges: ['logicalEdges', 'curIdx'],
            forwardsEdgeToUnsortedEdge: [
                'forwardsEdgeToUnsortedEdge',
                'forwardsEdgeToUnsortedEdge'
            ],
            edgeColors: ['edgeColors', 'edgeColor']
        },
        uniforms: {
            edgeOpacity: { uniformType: '1f', defaultValues: [0.3] },
            edgeZoomScalingFactor: { uniformType: '1f', defaultValues: [1.0] },
            viewportSize: { uniformType: '2f', defaultValues: [1.0, 1.0] },
            zoomScalingFactor: { uniformType: '1f', defaultValues: [1.0] },
            edgeCurvature: { uniformType: '1f', defaultValues: [0.05] },
            depthOcclusion: { uniformType: '1f', defaultValues: [0.0] }, //motion-only inter-edge occlusion (prepareFrame)
            depthIdMax: { uniformType: '1f', defaultValues: [1.0] },    //instance count, set by prepareFrame
            minPointSize: { uniformType: '1f', defaultValues: [8.0] },
            maxPointSize,
            maxScreenSize: { uniformType: '1f', defaultValues: [1.0] },
            arrowEdgeSegments: { uniformType: '1f', defaultValues: [9.0] },
            showArrows: { uniformType: '1f', defaultValues: [1.0] },
            //hairline floor in BUFFER px: 2 DISPLAY px * default pixelRatio 2;
            //the scheduler rewrites it from the live pixelRatio each settle
            minStrokeWidthPx: { uniformType: '1f', defaultValues: [4.0] },
            widthFade: { uniformType: '1f', defaultValues: [1.0] }
        },
        drawType: 'TRIANGLE_STRIP',
        instanced: true,
        verticesPerInstance: EDGE_STRIP_VERTS,
        glOptions: {}
    },
    //Instanced GPU arrowheads: one triangle per edge, tangent computed from
    //the shared edge instance buffers + height LUT (replaces the CPU-built
    //arrowculled in edgesdf mode)
    edgesdfarrow: {
        program: 'edgesdfarrow',
        triggers: ['panZoom', 'renderSceneFast', 'renderSceneFull'],
        bindings: {
            normalDir: ['arrowCornerDirs', 'normalDir'],
            srcNodeId: ['edgeEndpointIds', 'srcNodeId'],
            dstNodeId: ['edgeEndpointIds', 'dstNodeId'],
            dstColor: ['edgeStyleAttrs', 'dstColor'],
            shapeFactor: ['edgeStyleAttrs', 'shapeFactor'],
            dstPointSize: ['edgeStyleAttrs', 'dstPointSize']
        },
        uniforms: {
            edgeOpacity: { uniformType: '1f', defaultValues: [0.3] },
            tintWhite: { uniformType: '1f', defaultValues: [0.0] },
            viewportSize: { uniformType: '2f', defaultValues: [1.0, 1.0] },
            zoomScalingFactor: { uniformType: '1f', defaultValues: [1.0] },
            edgeCurvature: { uniformType: '1f', defaultValues: [0.05] },
            maxPointSize,
            minPointSize: { uniformType: '1f', defaultValues: [8.0] },
            maxScreenSize: { uniformType: '1f', defaultValues: [1.0] },
            //numRenderedSplits + 1; scheduler keeps it in sync with the config
            arrowEdgeSegments: { uniformType: '1f', defaultValues: [9.0] },
            //stroke width; scales the arrowhead so it stays proportional on fat edges
            edgeZoomScalingFactor: { uniformType: '1f', defaultValues: [1.0] }
        },
        drawType: 'TRIANGLES',
        instanced: true,
        verticesPerInstance: 3,
        glOptions: {}
    },
    //GPU arrowheads for the hover/selection overlays, sharing the subset
    //instance buffers with edgesdfhighlight/edgesdfselected -- identical
    //geometry to edgesdfarrow so overlays never ghost against main arrows
    edgesdfarrowhighlight: {
        program: 'edgesdfarrow',
        triggers: ['highlight', 'highlightDark'],
        bindings: {
            normalDir: ['arrowCornerDirs', 'normalDir'],
            srcNodeId: ['edgeHighlightEndpointIds', 'srcNodeId'],
            dstNodeId: ['edgeHighlightEndpointIds', 'dstNodeId'],
            dstColor: ['edgeHighlightStyleAttrs', 'dstColor'],
            shapeFactor: ['edgeHighlightStyleAttrs', 'shapeFactor'],
            dstPointSize: ['edgeHighlightStyleAttrs', 'dstPointSize']
        },
        uniforms: {
            //arrowhighlighted.vertex parity: opaque, 40/60 mix toward white
            edgeOpacity: { uniformType: '1f', defaultValues: [1.0] },
            tintWhite: { uniformType: '1f', defaultValues: [0.6] },
            viewportSize: { uniformType: '2f', defaultValues: [1.0, 1.0] },
            zoomScalingFactor: { uniformType: '1f', defaultValues: [1.0] },
            edgeCurvature: { uniformType: '1f', defaultValues: [0.05] },
            maxPointSize,
            minPointSize: { uniformType: '1f', defaultValues: [8.0] },
            maxScreenSize: { uniformType: '1f', defaultValues: [1.0] },
            arrowEdgeSegments: { uniformType: '1f', defaultValues: [9.0] },
            //stroke width; scales the arrowhead so it stays proportional on fat edges
            edgeZoomScalingFactor: { uniformType: '1f', defaultValues: [1.0] }
        },
        drawType: 'TRIANGLES',
        instanced: true,
        verticesPerInstance: 3,
        glOptions: drawInListOrder
    },
    edgesdfarrowselected: {
        program: 'edgesdfarrow',
        triggers: ['highlightDark'],
        bindings: {
            normalDir: ['arrowCornerDirs', 'normalDir'],
            srcNodeId: ['edgeSelectedEndpointIds', 'srcNodeId'],
            dstNodeId: ['edgeSelectedEndpointIds', 'dstNodeId'],
            dstColor: ['edgeSelectedStyleAttrs', 'dstColor'],
            shapeFactor: ['edgeSelectedStyleAttrs', 'shapeFactor'],
            dstPointSize: ['edgeSelectedStyleAttrs', 'dstPointSize']
        },
        uniforms: {
            edgeOpacity: { uniformType: '1f', defaultValues: [1.0] },
            tintWhite: { uniformType: '1f', defaultValues: [0.0] },
            viewportSize: { uniformType: '2f', defaultValues: [1.0, 1.0] },
            zoomScalingFactor: { uniformType: '1f', defaultValues: [1.0] },
            edgeCurvature: { uniformType: '1f', defaultValues: [0.05] },
            maxPointSize,
            minPointSize: { uniformType: '1f', defaultValues: [8.0] },
            maxScreenSize: { uniformType: '1f', defaultValues: [1.0] },
            arrowEdgeSegments: { uniformType: '1f', defaultValues: [9.0] },
            //stroke width; scales the arrowhead so it stays proportional on fat edges
            edgeZoomScalingFactor: { uniformType: '1f', defaultValues: [1.0] }
        },
        drawType: 'TRIANGLES',
        instanced: true,
        verticesPerInstance: 3,
        glOptions: drawInListOrder
    },
    //Picking companion to edgesdf: encodes the edge id from gl_InstanceID,
    //replacing edgepicking + the EDGE_INDEX buffer when the flag is on
    edgesdfpicking: {
        program: 'edgesdfpicking',
        triggers: ['picking'],
        bindings: {
            stripCoord: ['edgeStripCorners', 'stripCoord'],
            srcNodeId: ['edgeEndpointIds', 'srcNodeId'],
            dstNodeId: ['edgeEndpointIds', 'dstNodeId'],
            shapeFactor: ['edgeStyleAttrs', 'shapeFactor'],
            //self-loop rings size/offset off the destination node (keep the
            //geometry matching edgesdf so hits align with the visible loop)
            dstPointSize: ['edgeStyleAttrs', 'dstPointSize']
        },
        uniforms: {
            edgeZoomScalingFactor: { uniformType: '1f', defaultValues: [1.0] },
            viewportSize: { uniformType: '2f', defaultValues: [1.0, 1.0] },
            edgeCurvature: { uniformType: '1f', defaultValues: [0.05] },
            //numRenderedSplits + 1; scheduler keeps it in sync with the scene config
            pickMultiplier: { uniformType: '1f', defaultValues: [9.0] },
            //auto-refreshed from the hitmap texture config by setTextureUniforms
            textureScalingFactor: { uniformType: '1f', defaultValues: [hitmapScale] },
            //node sizing for the self-loop ring (parity with edgesdf)
            zoomScalingFactor: { uniformType: '1f', defaultValues: [1.0] },
            minPointSize: { uniformType: '1f', defaultValues: [8.0] },
            maxPointSize
        },
        drawType: 'TRIANGLE_STRIP',
        instanced: true,
        verticesPerInstance: EDGE_STRIP_VERTS,
        glOptions: pickingGlOpts,
        renderTarget: 'hitmap',
        readTarget: true
    },
    //SDF replacements for the LINES highlight/selection overlays in edgesdf
    //mode; both reuse the edgesdf program with per-item uniforms:
    //highlight = white at 0.7 (parity with edgehighlighted.vertex),
    //selected = own gradient colors at full opacity (parity with edgeselected)
    edgesdfhighlight: {
        program: 'edgesdf',
        triggers: ['highlight', 'highlightDark'],
        bindings: {
            stripCoord: ['edgeStripCorners', 'stripCoord'],
            srcNodeId: ['edgeHighlightEndpointIds', 'srcNodeId'],
            dstNodeId: ['edgeHighlightEndpointIds', 'dstNodeId'],
            srcColor: ['edgeHighlightStyleAttrs', 'srcColor'],
            dstColor: ['edgeHighlightStyleAttrs', 'dstColor'],
            shapeFactor: ['edgeHighlightStyleAttrs', 'shapeFactor'],
            dstPointSize: ['edgeHighlightStyleAttrs', 'dstPointSize']
        },
        uniforms: {
            edgeOpacity: { uniformType: '1f', defaultValues: [0.7] },
            edgeZoomScalingFactor: { uniformType: '1f', defaultValues: [1.0] },
            viewportSize: { uniformType: '2f', defaultValues: [1.0, 1.0] },
            zoomScalingFactor: { uniformType: '1f', defaultValues: [1.0] },
            edgeCurvature: { uniformType: '1f', defaultValues: [0.05] },
            minPointSize: { uniformType: '1f', defaultValues: [8.0] },
            maxPointSize,
            maxScreenSize: { uniformType: '1f', defaultValues: [1.0] },
            arrowEdgeSegments: { uniformType: '1f', defaultValues: [9.0] },
            showArrows: { uniformType: '1f', defaultValues: [1.0] },
            //hairline floor in BUFFER px: 2 DISPLAY px * default pixelRatio 2;
            //the scheduler rewrites it from the live pixelRatio each settle
            minStrokeWidthPx: { uniformType: '1f', defaultValues: [4.0] },
            widthFade: { uniformType: '1f', defaultValues: [0.0] }
        },
        drawType: 'TRIANGLE_STRIP',
        instanced: true,
        verticesPerInstance: EDGE_STRIP_VERTS,
        glOptions: drawInListOrder
    },
    edgesdfselected: {
        program: 'edgesdf',
        triggers: ['highlightDark'],
        bindings: {
            stripCoord: ['edgeStripCorners', 'stripCoord'],
            srcNodeId: ['edgeSelectedEndpointIds', 'srcNodeId'],
            dstNodeId: ['edgeSelectedEndpointIds', 'dstNodeId'],
            srcColor: ['edgeSelectedStyleAttrs', 'srcColor'],
            dstColor: ['edgeSelectedStyleAttrs', 'dstColor'],
            shapeFactor: ['edgeSelectedStyleAttrs', 'shapeFactor'],
            dstPointSize: ['edgeSelectedStyleAttrs', 'dstPointSize']
        },
        uniforms: {
            edgeOpacity: { uniformType: '1f', defaultValues: [1.0] },
            edgeZoomScalingFactor: { uniformType: '1f', defaultValues: [1.0] },
            viewportSize: { uniformType: '2f', defaultValues: [1.0, 1.0] },
            zoomScalingFactor: { uniformType: '1f', defaultValues: [1.0] },
            edgeCurvature: { uniformType: '1f', defaultValues: [0.05] },
            minPointSize: { uniformType: '1f', defaultValues: [8.0] },
            maxPointSize,
            maxScreenSize: { uniformType: '1f', defaultValues: [1.0] },
            arrowEdgeSegments: { uniformType: '1f', defaultValues: [9.0] },
            showArrows: { uniformType: '1f', defaultValues: [1.0] },
            //hairline floor in BUFFER px: 2 DISPLAY px * default pixelRatio 2;
            //the scheduler rewrites it from the live pixelRatio each settle
            minStrokeWidthPx: { uniformType: '1f', defaultValues: [4.0] },
            widthFade: { uniformType: '1f', defaultValues: [0.0] }
        },
        drawType: 'TRIANGLE_STRIP',
        instanced: true,
        verticesPerInstance: EDGE_STRIP_VERTS,
        glOptions: drawInListOrder
    },
    edgeselected: {
        program: 'edgeselected',
        triggers: ['highlightDark'],
        bindings: {
            curPos: ['selectedMidSpringsPos', 'curPos'],
            edgeColor: ['selectedMidEdgesColors', 'midEdgeColor'],
            startPos: ['selectedMidSpringsStarts', 'startPos'],
            endPos: ['selectedMidSpringsEnds', 'endPos']
        },
        otherBuffers: {
            logicalEdges: ['logicalEdges', 'curIdx'],
            forwardsEdgeToUnsortedEdge: [
                'forwardsEdgeToUnsortedEdge',
                'forwardsEdgeToUnsortedEdge'
            ],
            edgeColor: ['selectedMidEdgesColors', 'midEdgeColor'],
            startPos: ['selectedMidSpringsStarts', 'startPos'],
            endPos: ['selectedMidSpringsEnds', 'endPos']
        },
        uniforms: {
            isOpaque: { uniformType: '1f', defaultValues: [0.0] }
        },
        drawType: 'LINES',
        glOptions: {}
    },
    edgehighlight: {
        program: 'edgehighlight',
        triggers: ['highlight', 'highlightDark'],
        bindings: {
            curPos: ['highlightedEdgesPos', 'curPos']
        },
        drawType: 'LINES',
        glOptions: {}
    },
    arrowculled: {
        program: 'arrow',
        triggers: ['panZoom', 'renderSceneFull'],
        bindings: {
            startPos: ['arrowStartPos', 'curPos'],
            endPos: ['arrowEndPos', 'curPos'],
            normalDir: ['arrowNormalDir', 'normalDir'],
            arrowColor: ['arrowColors', 'arrowColor'],
            pointSize: ['arrowPointSizes', 'pointSize']
        },
        uniforms: {
            edgeOpacity: { uniformType: '1f', defaultValues: [0.3] },
            zoomScalingFactor: { uniformType: '1f', defaultValues: [1.0] },
            maxPointSize,
            maxScreenSize: { uniformType: '1f', defaultValues: [1.0] },
            maxCanvasSize: { uniformType: '1f', defaultValues: [1.0] }
        },
        drawType: 'TRIANGLES',
        glOptions: {
            //'depthFunc': [['LESS']]
        }
    },
    arrowselected: {
        program: 'arrow',
        triggers: ['panZoom', 'highlightDark'],
        bindings: {
            startPos: ['selectedArrowStartPos', 'curPos'],
            endPos: ['selectedArrowEndPos', 'curPos'],
            normalDir: ['selectedArrowNormalDir', 'normalDir'],
            arrowColor: ['selectedArrowColors', 'arrowColor'],
            pointSize: ['selectedArrowPointSizes', 'pointSize']
        },
        uniforms: {
            edgeOpacity: { uniformType: '1f', defaultValues: [0.3] },
            zoomScalingFactor: { uniformType: '1f', defaultValues: [1.0] },
            maxPointSize,
            maxScreenSize: { uniformType: '1f', defaultValues: [1.0] },
            maxCanvasSize: { uniformType: '1f', defaultValues: [1.0] }
        },
        drawType: 'TRIANGLES',
        glOptions: {
            //'depthFunc': [['LESS']]
        }
    },
    arrowhighlight: {
        program: 'arrowhighlight',
        triggers: ['panZoom', 'highlight', 'highlightDark'],
        bindings: {
            startPos: ['highlightedArrowStartPos', 'curPos'],
            endPos: ['highlightedArrowEndPos', 'curPos'],
            normalDir: ['highlightedArrowNormalDir', 'normalDir'],
            arrowColor: ['highlightedArrowPointColors', 'arrowColor'],
            pointSize: ['highlightedArrowPointSizes', 'pointSize']
        },
        uniforms: {
            zoomScalingFactor: { uniformType: '1f', defaultValues: [1.0] },
            maxPointSize,
            maxScreenSize: { uniformType: '1f', defaultValues: [1.0] },
            maxCanvasSize: { uniformType: '1f', defaultValues: [1.0] }
        },
        drawType: 'TRIANGLES',
        glOptions: {
            //'depthFunc': [['LESS']]
        }
    },
    edgepicking: {
        program: 'edgepicking',
        triggers: ['picking'],
        bindings: {
            curPos: ['midSpringsPos', 'curPos'],
            startPos: ['midSpringsStarts', 'startPos'],
            endPos: ['midSpringsEnds', 'endPos'],
            edgeColor: ['edgeIndices', 'edgeColor']
        },
        uniforms: {},
        drawType: 'LINES',
        glOptions: pickingGlOpts,
        renderTarget: 'hitmap',
        readTarget: true
    },
    pointdepth: {
        program: 'pointdepth',
        triggers: ['panZoom', 'renderSceneFast', 'renderSceneFull'],
        bindings: {
            pointSize: ['pointSizes', 'pointSize'],
            pointColor: ['pointColors', 'pointColor']
        },
        uniforms: pointCulledUniforms,
        drawType: 'POINTS',
        glOptions: { colorMask: [[false, false, false, false]] }
    },
    pointculled: {
        program: 'pointculled',
        triggers: ['panZoom', 'renderSceneFast', 'renderSceneFull'],
        bindings: {
            pointSize: ['pointSizes', 'pointSize'],
            pointColor: ['pointColors', 'pointColor']
        },
        //no attribute binds curPoints anymore (positions come from the node
        //texture via gl_VertexID), but the model must stay in the scene:
        //loadBuffers drops arriving stream data with no declared model, and
        //the host cache it feeds (labels/gestures/overlay gathers) is
        //syncDataTextures' source
        otherBuffers: {
            curPoints: ['curPoints', 'curPos']
        },
        uniforms: pointCulledUniforms,
        drawType: 'POINTS',
        glOptions: drawInListOrder
    },
    pointculledoutline: {
        program: 'pointculled',
        triggers: ['panZoom', 'renderSceneFull'],
        bindings: {
            pointSize: ['pointSizes', 'pointSize'],
            pointColor: ['pointColors', 'pointColor']
        },
        uniforms: {
            ...pointCulledUniforms,
            ...{
                stroke: { uniformType: '1f', defaultValues: [STROKE_WIDTH] }
            }
        },
        drawType: 'POINTS',
        glOptions: drawInListOrder
    },
    pointselected: {
        program: 'pointselected',
        triggers: ['highlightDark'],
        bindings: {
            nodeId: ['pointSelectedNodeIds', 'nodeId'],
            pointSize: ['selectedPointSizes', 'pointSize'],
            pointColor: ['selectedPointColors', 'pointColor']
        },
        uniforms: {
            ...pointCulledUniforms,
            //move-points drag preview shift (see renderMovePointsOverlay)
            posOffset: { uniformType: '2f', defaultValues: [0.0, 0.0] }
        },
        drawType: 'POINTS',
        glOptions: drawInListOrder
    },
    pointselectedoutline: {
        program: 'pointselected',
        triggers: ['highlightDark'],
        bindings: {
            nodeId: ['pointSelectedNodeIds', 'nodeId'],
            pointSize: ['selectedPointSizes', 'pointSize'],
            pointColor: ['selectedPointColors', 'pointColor']
        },
        uniforms: {
            ...pointCulledUniforms,
            ...{
                stroke: { uniformType: '1f', defaultValues: [STROKE_WIDTH] },
                //move-points drag preview shift (see renderMovePointsOverlay)
                posOffset: { uniformType: '2f', defaultValues: [0.0, 0.0] }
            }
        },
        drawType: 'POINTS',
        glOptions: drawInListOrder
    },
    pointhighlight: {
        program: 'pointhighlight',
        triggers: ['highlight', 'highlightDark'],
        bindings: {
            nodeId: ['pointHighlightNodeIds', 'nodeId'],
            pointSize: ['highlightedPointsSizes', 'pointSize'],
            pointColor: ['highlightedPointsColors', 'pointColor']
        },
        otherBuffers: {
            forwardsEdgeStartEndIdxs: ['forwardsEdgeStartEndIdxs', 'curIdx']
        },
        uniforms: pointCulledUniforms,
        drawType: 'POINTS',
        glOptions: drawInListOrder
    },
    pointhighlightoutline: {
        program: 'pointhighlight',
        triggers: ['highlight', 'highlightDark'],
        bindings: {
            nodeId: ['pointHighlightNodeIds', 'nodeId'],
            pointSize: ['highlightedPointsSizes', 'pointSize'],
            pointColor: ['highlightedPointsColors', 'pointColor']
        },
        uniforms: {
            ...pointCulledUniforms,
            ...{
                stroke: { uniformType: '1f', defaultValues: [STROKE_WIDTH] }
            }
        },
        drawType: 'POINTS',
        glOptions: drawInListOrder
    },
    pointculledtexture: {
        program: 'pointculled',
        triggers: ['marquee'],
        bindings: {
            pointSize: ['pointSizes', 'pointSize'],
            pointColor: ['pointColors', 'pointColor']
        },
        uniforms: pointCulledUniforms,
        drawType: 'POINTS',
        glOptions: { clearColor: [[1, 1, 1, 0.0]] },
        renderTarget: 'pointTexture',
        readTarget: true
    },
    pointoutlinetexture: {
        program: 'pointculled',
        triggers: ['marquee'],
        bindings: {
            pointSize: ['pointSizes', 'pointSize'],
            pointColor: ['pointColors', 'pointColor']
        },
        uniforms: {
            ...pointCulledUniforms,
            ...{
                stroke: { uniformType: '1f', defaultValues: [STROKE_WIDTH] }
            }
        },
        drawType: 'POINTS',
        glOptions: { clearColor: [[1, 1, 1, 0.0]] },
        renderTarget: 'pointTexture',
        readTarget: false
    },
    pointpicking: {
        program: 'pointpicking',
        triggers: ['picking'],
        bindings: {
            pointSize: ['pointSizes', 'pointSize'],
            pointColor: ['vertexIndices', 'pointColor']
        },
        uniforms: {
            zoomScalingFactor: { uniformType: '1f', defaultValues: [1.0] },
            textureScalingFactor: { uniformType: '1f', defaultValues: [hitmapScale] },
            maxPointSize
        },
        drawType: 'POINTS',
        glOptions: pickingGlOpts,
        renderTarget: 'hitmap',
        readTarget: true
    },
    pointsampling: {
        program: 'pointpicking',
        triggers: ['picking'],
        bindings: {
            pointSize: ['pointSizes', 'pointSize'],
            pointColor: ['vertexIndices', 'pointColor']
        },
        uniforms: {
            zoomScalingFactor: { uniformType: '1f', defaultValues: [1.0] },
            textureScalingFactor: { uniformType: '1f', defaultValues: [hitmapDownScale] },
            maxPointSize
        },
        drawType: 'POINTS',
        glOptions: pickingGlOpts,
        renderTarget: 'pointHitmapDownsampled',
        readTarget: true
    },
    fullscreen: {
        program: 'fullscreen',
        triggers: ['highlight'],
        bindings: {
            vertexPosition: ['fullscreenCoordinates', 'vertexPosition']
        },
        drawType: 'TRIANGLES',
        //steadyStateTexture holds PREMULTIPLIED color: the scene blends over a
        //transparent clear, so the buffer copied into it is rgb*a already. The
        //scene-default (SRC_ALPHA, 1-SRC_ALPHA) blend multiplied by alpha a
        //SECOND time on redisplay, quadratically darkening every
        //semi-transparent pixel (AA fringes, low-opacity edges) in the
        //presented frame. Premultiplied-over is the correct composite.
        glOptions: {
            ...drawInListOrder,
            blendFuncSeparate: [['ONE', 'ONE_MINUS_SRC_ALPHA', 'ONE', 'ONE_MINUS_SRC_ALPHA']]
        }
    },
    fullscreenDark: {
        program: 'fullscreenDark',
        triggers: ['highlightDark'],
        bindings: {
            vertexPosition: ['fullscreenCoordinates', 'vertexPosition']
        },
        drawType: 'TRIANGLES',
        glOptions: drawInListOrder
    },
    // Because we can't tell renderer to make a texture unless we write to it in an item
    // TODO: Add this functionality and kill fullscreenDummy
    fullscreenDummy: {
        program: 'fullscreen',
        triggers: [],
        bindings: {
            vertexPosition: ['fullscreenCoordinates', 'vertexPosition']
        },
        drawType: 'TRIANGLES',
        //keep in sync with fullscreen: premultiplied-over (see comment there)
        glOptions: {
            ...drawInListOrder,
            blendFuncSeparate: [['ONE', 'ONE_MINUS_SRC_ALPHA', 'ONE', 'ONE_MINUS_SRC_ALPHA']]
        },
        renderTarget: 'steadyStateTexture',
        readTarget: true
    }
};
